import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRightLeft,
  CircleCheck,
  ClipboardCheck,
  Clock,
  FilePlus2,
  ListChecks,
  QrCode,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useHomeSummary, useMyStats, useMyTasks, useSubmissions } from '@/lib/queries'
import { Badge, Card, CardTitle, EmptyState, Skeleton } from '@/components/ui'
import { KpiTile } from '@/components/KpiTile'
import { Leaderboard } from '@/components/Leaderboard'
import { Hint } from '@/components/Hint'
import { cn, formatDate, formatNumber, formatPercent, formatRelative } from '@/lib/utils'
import { addYears, calendarDate, today } from '@/lib/timezone'
import { scoreColor } from '@/lib/fiveSTarget'
import { moduleEnabled } from '@/lib/modules'
import { plantScope } from '@/lib/plantAccess'
import {
  FIVE_S_BOARD,
  TASKS,
  newWalkHref,
  submissionHref,
  submissionsHref,
} from '@/lib/listLinks'
import {
  activityHref,
  coverage,
  daysLate,
  missingByPlant,
  monthStart,
  tasksNeedingMe,
} from '@/lib/homeView'
import { useI18n, useT } from '@/i18n/I18nProvider'
import type { HomeActivity, HomeFiveS, HomeQueue, MyNumbers, Submission, Task, User } from '@/types/api'

/**
 * Two first screens, chosen by role, both answering "what needs me today".
 *
 * The two audiences want opposite things from this screen. A worker opens the
 * app standing next to a machine, with one job — report what they are looking
 * at — and then wants to know what became of what they reported. A supervisor
 * opens it at a desk to find the work that is waiting on them, which is a set
 * of numbers and the few items behind the worst of them.
 *
 * ## What is not here any more
 *
 * Five cards linking to Red čekanja, Zadaci, 5S, Analitika and Administracija,
 * a "Nova prijava" button and an "Otvori red čekanja" button. The sidebar and
 * "+ Novo" in the header are on every screen now, so each of those was a
 * second route to the same place, standing between the reader and the
 * numbers. The red banner went too: it repeated the "van roka" tile word for
 * word, one row lower.
 *
 * ## Every number is a link
 *
 * To the list it counts, filtered to exactly that — see `lib/listLinks.ts`.
 * A dashboard figure that has to be found again by hand on another screen is
 * a figure nobody follows up.
 */

/** Twelve months back, as a date string, from today on the company's clock. */
function aYearAgo(): string {
  return addYears(today(), -1)
}

/*
 * Both dashboards drop what the company does not have.
 *
 * Home is the one screen every company keeps whatever is switched off, so it
 * cannot be guarded like the others — it has to shrink instead. A report
 * button, a queue or a 5S card for a module that is off would each lead to
 * "not switched on", which is a dead end on the screen people open first.
 */
function modulesOf(user: User) {
  const modules = user.company?.modules

  return {
    submissions: moduleEnabled(modules, 'submissions'),
    fiveS: moduleEnabled(modules, 'fiveS'),
  }
}

export function HomePage() {
  const { user } = useAuth()

  if (!user) return null

  return user.isManager ? <ManagerHome user={user} /> : <WorkerHome user={user} />
}

function Greeting({ user }: { user: User }) {
  const t = useT()

  return (
    <header>
      <h1 className="text-xl font-semibold sm:text-2xl">
        {t('home.greeting', { name: user.name.split(' ')[0] })}
      </h1>
      <p className="muted text-sm">{user.roleLabel}</p>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Manager / responsible / maintenance / commission / admin
// ---------------------------------------------------------------------------

function ManagerHome({ user }: { user: User }) {
  const { t, tp } = useI18n()
  const has = modulesOf(user)
  const summary = useHomeSummary()
  const tasks = useMyTasks(true, { enabled: has.submissions })

  const queue = summary.data?.queue ?? null
  const fiveS = summary.data?.fiveS ?? null
  const day = today()
  const mine = tasksNeedingMe(tasks.data?.data ?? [], day)
  const myTaskCount = tasks.data?.meta.total ?? 0

  return (
    <div className="space-y-4">
      <Greeting user={user} />

      <section aria-labelledby="home-today" className="space-y-3">
        <h2 id="home-today" className="muted text-xs font-medium tracking-wide uppercase">
          {t('home.today')}
        </h2>

        {has.submissions && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {queue ? (
              <>
                <KpiTile
                  label={t('home.stat.overdue')}
                  value={formatNumber(queue.overdue.count)}
                  to={submissionsHref({ tab: 'van-roka' })}
                  tone={queue.overdue.count > 0 ? 'bad' : 'good'}
                  hint={
                    queue.overdue.count > 0
                      ? t('home.stat.overdue.needsAttention')
                      : t('home.stat.overdue.allOnTime')
                  }
                  explain={t('home.stat.overdue.explain')}
                />
                <KpiTile
                  label={t('home.stat.fresh')}
                  value={formatNumber(queue.fresh)}
                  to={submissionsHref({ tab: 'nove' })}
                  hint={t('home.stat.fresh.hint')}
                />
                <KpiTile
                  label={t('home.stat.unassigned')}
                  value={formatNumber(queue.unassigned)}
                  to={submissionsHref({ tab: 'otvorene', unassigned: true })}
                  tone={queue.unassigned > 0 ? 'warn' : 'neutral'}
                  hint={t('home.stat.unassigned.hint', { open: formatNumber(queue.open) })}
                  explain={t('home.stat.openSubmissions.explain')}
                />
              </>
            ) : (
              <TileSkeletons count={3} failed={summary.isError} />
            )}
            <KpiTile
              label={t('home.stat.myTasks')}
              value={formatNumber(myTaskCount)}
              to={TASKS}
              tone={mine.overdue > 0 ? 'bad' : 'neutral'}
              hint={
                mine.overdue > 0
                  ? tp('home.tasks.lateCount', mine.overdue)
                  : mine.soon.length > 0
                    ? tp('home.tasks.soonCount', mine.soon.length)
                    : t('home.tasks.nothingSoon')
              }
              explain={t('home.stat.myTasks.explain')}
            />
          </div>
        )}
      </section>

      {/*
        Two columns on a desk, in reading order on a phone: the late reports,
        5S, my tasks, then what happened. On a desk the activity list drops
        under the late reports and the tasks under 5S, so the two columns are
        each one kind of thing — the queue on the left, my own work on the right.
      */}
      <div className="grid gap-4 lg:grid-cols-3">
        {has.submissions && queue && <OverdueCard queue={queue} className="lg:col-span-2" />}

        {has.fiveS && fiveS && <FiveSCard fiveS={fiveS} canEvaluate={user.canEvaluate} />}

        {/* Always on a desk, for a supervisor: "nothing is assigned to you"
            is an answer, and the column it sits in would otherwise end at 5S.
            Not on a phone when it is empty — the tile above already says 0,
            and an empty card there is a screen of scrolling to the activity. */}
        {has.submissions && tasks.data && (
          <MyTasksCard
            tasks={tasks.data.data.slice(0, 5)}
            className={cn('lg:col-start-3', tasks.data.data.length === 0 && 'max-lg:hidden')}
          />
        )}

        {summary.data && (
          <ActivityCard
            items={summary.data.activity}
            className="lg:col-span-2 lg:col-start-1 lg:row-start-2"
          />
        )}
      </div>
    </div>
  )
}

function TileSkeletons({ count, failed }: { count: number; failed: boolean }) {
  // A failed summary leaves the tiles out rather than showing zeros: a nought
  // that means "could not ask" reads as "nothing is late".
  if (failed) return null

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="h-28 rounded-2xl" />
      ))}
    </>
  )
}

/**
 * The reports longest past their deadline, by name.
 *
 * Three, because the tile above already says how many there are, and what a
 * supervisor needs next is which ones — not a second copy of the queue.
 */
function OverdueCard({ queue, className }: { queue: HomeQueue; className?: string }) {
  const { t, tp } = useI18n()
  const day = today()
  const { count, top } = queue.overdue

  return (
    <Card className={className}>
      <CardTitle
        action={
          count > 0 ? (
            <CardLink to={submissionsHref({ tab: 'van-roka' })}>
              {t('home.overdue.all', { count: formatNumber(count) })}
            </CardLink>
          ) : undefined
        }
      >
        {t('home.overdue.title')}
      </CardTitle>

      {top.length === 0 ? (
        <EmptyState
          icon={<CircleCheck className="size-6" aria-hidden />}
          title={t('home.overdue.empty')}
          description={t('home.overdue.emptyText')}
        />
      ) : (
        <ul className="divide-border -my-1 divide-y">
          {top.map((report) => (
            <li key={report.id}>
              <Link
                to={submissionHref(report.id)}
                className="hover:bg-muted focus-visible:ring-ring/60 -mx-2 flex min-h-control items-center justify-between gap-3 rounded-xl px-2 py-2.5 outline-none transition-colors focus-visible:ring-[3px]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{report.title || report.typeLabel}</p>
                  <p className="muted truncate text-xs">
                    {report.referenceNo}
                    {report.workCenter && ` · ${report.workCenter.name}`}
                    {' · '}
                    {report.assignedTo ? report.assignedTo.name : t('home.overdue.nobody')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <Badge color={report.priorityColor}>{report.priorityLabel}</Badge>
                  {report.dueAt && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                      <Clock className="size-3.5" aria-hidden />
                      {tp('home.overdue.late', daysLate(calendarDate(report.dueAt), day))}
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/**
 * How far through the month's walks the plant is, and which machines are left.
 *
 * The machines link straight to a walk of that machine for somebody who can
 * walk it — the one thing anybody does after reading "Hoblanje još nije
 * obiđena". For somebody who cannot, they are a list to read, not buttons
 * that would end on a refusal.
 */
function FiveSCard({ fiveS, canEvaluate }: { fiveS: HomeFiveS; canEvaluate: boolean }) {
  const { t, tp } = useI18n()
  const done = coverage(fiveS)
  const groups = missingByPlant(fiveS.missing)
  const SHOWN = 6
  let left = SHOWN

  return (
    <Card>
      <CardTitle action={<CardLink to={FIVE_S_BOARD}>{t('home.fiveS.board')}</CardLink>}>
        {t('home.fiveS.month', { month: fiveS.monthLabel })}
      </CardTitle>

      {done.percent === null ? (
        <p className="muted text-sm">{t('home.fiveS.noMachines')}</p>
      ) : (
        <>
          <Link
            to={FIVE_S_BOARD}
            className="focus-visible:ring-ring/60 block rounded-xl outline-none focus-visible:ring-[3px]"
          >
            <p className="text-sm">
              <span className="font-heading text-2xl font-semibold tabular-nums">
                {formatNumber(done.walked)}
              </span>{' '}
              {tp('home.fiveS.walkedOf', done.machines, { walked: formatNumber(done.walked) })}
            </p>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={done.machines}
              aria-valuenow={done.walked}
              aria-label={t('home.fiveS.progress')}
              className="bg-muted mt-2 h-2 overflow-hidden rounded-full"
            >
              <div
                className={cn('h-full rounded-full', done.done ? 'bg-emerald-500' : 'bg-[var(--progress-3)]')}
                style={{ width: `${done.percent}%` }}
              />
            </div>
          </Link>

          {fiveS.average !== null && (
            <p className="muted mt-2 text-xs">
              {t('home.fiveS.average', { value: formatPercent(fiveS.average) })}
            </p>
          )}

          {done.done ? (
            <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
              <CircleCheck className="size-4 shrink-0" aria-hidden />
              {t('home.fiveS.allWalked')}
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium">{t('home.fiveS.toWalk')}</p>
              {groups.map((group) => {
                const shown = group.machines.slice(0, Math.max(0, left))
                left -= shown.length

                if (shown.length === 0) return null

                return (
                  <div key={group.plant?.id ?? 'none'}>
                    {groups.length > 1 && group.plant && (
                      <p className="muted mb-1 text-xs">{group.plant.name}</p>
                    )}
                    <ul className="flex flex-wrap gap-2">
                      {shown.map((machine) => (
                        <li key={machine.workCenterId}>
                          {canEvaluate ? (
                            <Link
                              to={newWalkHref(machine.workCenterId)}
                              className="bg-muted hover:bg-muted/70 focus-visible:ring-ring/60 inline-flex min-h-control items-center gap-1.5 rounded-xl px-3 text-sm font-medium outline-none focus-visible:ring-[3px]"
                            >
                              <ClipboardCheck className="text-brand-600 size-4" aria-hidden />
                              {machine.name}
                            </Link>
                          ) : (
                            <span className="bg-muted inline-flex min-h-control items-center rounded-xl px-3 text-sm">
                              {machine.name}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
              {fiveS.missing.length > SHOWN && (
                <CardLink to={FIVE_S_BOARD}>
                  {t('home.fiveS.more', { count: formatNumber(fiveS.missing.length - SHOWN) })}
                </CardLink>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

/**
 * What happened lately in the reader's plants.
 *
 * No verbs with a subject: "Amra je prijavila" needs the reporter's gender,
 * which the application does not know and should not guess. Each line names
 * the event as a noun — "Nova prijava", "5S obilazak" — and the person after.
 */
function ActivityCard({ items, className }: { items: HomeActivity[]; className?: string }) {
  const t = useT()
  const { user } = useAuth()
  const scope = plantScope(user)

  return (
    <Card className={className}>
      <CardTitle>
        {t(
          scope === 'all'
            ? 'home.activity.title.all'
            : scope === 'several'
              ? 'home.activity.title.several'
              : 'home.activity.title',
        )}
      </CardTitle>

      {items.length === 0 ? (
        <EmptyState title={t('home.activity.empty')} description={t('home.activity.emptyText')} />
      ) : (
        <ul className="divide-border -my-1 divide-y">
          {items.map((item, index) => (
            <li key={`${item.kind}-${item.at}-${index}`}>
              <Link
                to={activityHref(item)}
                className="hover:bg-muted focus-visible:ring-ring/60 -mx-2 flex min-h-control items-center gap-3 rounded-xl px-2 py-2 outline-none transition-colors focus-visible:ring-[3px]"
              >
                <ActivityIcon kind={item.kind} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    <span className="font-medium">{activityWhat(item, t)}</span>
                    {item.kind !== 'walked' && (
                      <span className="muted"> · {item.submission.title || item.submission.typeLabel}</span>
                    )}
                  </p>
                  <p className="muted truncate text-xs">
                    {[item.actor, item.workCenter, formatRelative(item.at)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {item.kind === 'moved' && <Badge color={item.status.color}>{item.status.label}</Badge>}
                {item.kind === 'walked' && (
                  <Badge color={scoreColor(item.evaluation.percentage)}>
                    {formatPercent(item.evaluation.percentage)}
                  </Badge>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function activityWhat(item: HomeActivity, t: ReturnType<typeof useT>): string {
  switch (item.kind) {
    case 'reported':
      return t('home.activity.reported', { ref: item.submission.referenceNo })
    case 'moved':
      return t('home.activity.moved', { ref: item.submission.referenceNo })
    case 'walked':
      return t('home.activity.walked')
  }
}

function ActivityIcon({ kind }: { kind: HomeActivity['kind'] }) {
  const Icon = kind === 'reported' ? FilePlus2 : kind === 'moved' ? ArrowRightLeft : ClipboardCheck

  return (
    <span className="bg-muted text-brand-600 grid size-8 shrink-0 place-items-center rounded-lg">
      <Icon className="size-4" aria-hidden />
    </span>
  )
}

// ---------------------------------------------------------------------------
// Worker
// ---------------------------------------------------------------------------

/**
 * Deliberately small.
 *
 * The report button first and biggest, then what became of what they
 * reported, their tasks, and their own rating for the month with what it rests
 * on. No queue and no work-centre breakdowns — a worker can act on none of
 * it, and every row of it is a row between them and the report button.
 */
function WorkerHome({ user }: { user: User }) {
  const t = useT()
  const has = modulesOf(user)
  const day = today()
  // This month's window: the rating is "how am I doing now", and the report
  // counts in the same reply are lifetime figures whatever the window.
  const stats = useMyStats({ from: monthStart(day), to: day })
  const tasks = useMyTasks(true, { enabled: has.submissions })
  const recent = useSubmissions({ perPage: 5 }, { enabled: has.submissions })

  return (
    <div className="space-y-4">
      <Greeting user={user} />

      {/* The thing a worker came here to do, at the top and big enough to hit
          with a glove on. Scanning is the second way in, so it is smaller. */}
      <section className="grid gap-3 sm:grid-cols-3">
        {has.submissions && (
          <BigAction
            to="/prijave/nova"
            icon={<ListChecks className="size-7" />}
            title={t('home.report.title')}
            text={t('home.report.text')}
            primary
            className="sm:col-span-2"
          />
        )}
        <BigAction
          to="/skeniraj"
          icon={<QrCode className="size-6" />}
          title={t('home.scan.title')}
          text={t('home.scan.text')}
          className={has.submissions ? undefined : 'sm:col-span-3'}
        />
      </section>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {has.submissions && (
          <MyReportsCard
            submissions={recent.data?.data ?? []}
            loading={recent.isPending}
            open={stats.data?.open}
            resolved={stats.data?.resolved}
          />
        )}

        <div className="space-y-4">
          {tasks.data && tasks.data.data.length > 0 && (
            <MyTasksCard tasks={tasks.data.data.slice(0, 5)} />
          )}
          {stats.data && <MyRatingCard mine={stats.data.mine} points={stats.data.points} />}
        </div>
      </div>

      {/*
        The ranking, on the screen a worker actually opens. Twelve months, so a
        bad fortnight does not decide somebody's position — and so the figure
        moves slowly enough to be worth acting on. The card above is this
        month's; the window is said on both.
      */}
      <Leaderboard range={{ from: aYearAgo() }} />
    </div>
  )
}

/**
 * What became of what I reported.
 *
 * The newest five with where each one stands, and the two counts that answer
 * "is anybody doing anything with them". Both counts, and the card's link,
 * open the list on "Moje prijave".
 */
function MyReportsCard({
  submissions,
  loading,
  open,
  resolved,
}: {
  submissions: Submission[]
  loading: boolean
  open: number | undefined
  resolved: number | undefined
}) {
  const t = useT()
  const mineHref = submissionsHref({ tab: 'moje' })

  return (
    <Card>
      <CardTitle action={<CardLink to={mineHref}>{t('home.mine.all')}</CardLink>}>
        {t('home.mine.title')}
      </CardTitle>

      {open !== undefined && resolved !== undefined && (
        <dl className="mb-3 grid grid-cols-2 gap-2">
          <MiniStat to={mineHref} label={t('home.stat.inProgress')} value={formatNumber(open)} />
          <MiniStat to={mineHref} label={t('home.mine.resolved')} value={formatNumber(resolved)} />
        </dl>
      )}

      {loading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : submissions.length === 0 ? (
        <EmptyState title={t('home.recent.empty')} description={t('home.recent.emptyText')} />
      ) : (
        <ul className="divide-border -my-1 divide-y">
          {submissions.map((submission) => (
            <li key={submission.id}>
              <Link
                to={submissionHref(submission.id)}
                className="hover:bg-muted focus-visible:ring-ring/60 -mx-2 flex min-h-control items-center justify-between gap-3 rounded-xl px-2 py-2.5 outline-none transition-colors focus-visible:ring-[3px]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{submission.title || submission.typeLabel}</p>
                  <p className="muted truncate text-xs">
                    {submission.referenceNo}
                    {submission.workCenter && ` · ${submission.workCenter.name}`}
                    {' · '}
                    {formatRelative(submission.createdAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  {submission.isOverdue && <Badge color="rose">{t('home.tasks.overdue')}</Badge>}
                  <Badge color={submission.statusColor}>{submission.statusLabel}</Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function MiniStat({ to, label, value }: { to: string; label: string; value: string }) {
  return (
    <Link
      to={to}
      className="bg-muted/60 hover:bg-muted focus-visible:ring-ring/60 rounded-xl px-3 py-2 outline-none transition-colors focus-visible:ring-[3px]"
    >
      <dt className="muted text-xs">{label}</dt>
      <dd className="font-heading text-xl font-semibold tabular-nums">{value}</dd>
    </Link>
  )
}

/**
 * My rating this month, and what it rests on.
 *
 * The same score the ranking prints, scored the same way — but for the month,
 * and with each part spelled out in the counts behind it. "Ocjena 72" invites
 * "why"; "5S 78% from 3 walks, 2 of 3 tasks done, 1 accepted proposal" is the
 * answer, and it is the answer somebody can do something about this week.
 *
 * The plant's 5S average sits beside the 5S part, for the reason the old
 * "Centri koje održavaš" card gave: a percentage on its own invites "is that
 * good" and cannot answer it. It is not a ranking, and the hint says so.
 */
function MyRatingCard({ mine, points }: { mine: MyNumbers; points: number }) {
  const { t, tp } = useI18n()
  const { user } = useAuth()
  const { rating, formula } = mine
  const scope = plantScope(user)
  const part = (key: 'fiveS' | 'followThrough') =>
    rating.components.find((component) => component.key === key)?.value ?? null

  const plantLabel = t(
    scope === 'all' ? 'home.fiveS.plant.all' : scope === 'several' ? 'home.fiveS.plant.several' : 'home.fiveS.plant',
  )

  return (
    <Card>
      <CardTitle>
        <span className="inline-flex items-center gap-2">
          {t('home.rating.title')}
          <Hint>
            {t('leaderboard.formula', {
              fiveS: formatPercent(formula.fiveS * 100),
              tasks: formatPercent(formula.followThrough * 100),
              bonus: formatNumber(formula.maxBonus),
              accepted: formatNumber(formula.acceptedForFullMarks),
            })}
          </Hint>
        </span>
      </CardTitle>

      <div className="flex items-baseline gap-3">
        <p className="font-heading text-4xl font-semibold tabular-nums">
          {rating.score === null ? '—' : formatPercent(rating.score)}
        </p>
        {rating.score !== null && rating.thin && (
          <span className="muted text-xs">{t('leaderboard.thin')}</span>
        )}
      </div>
      {rating.score === null && <p className="muted mt-1 text-sm">{t('home.rating.empty')}</p>}

      <dl className="divide-border mt-3 divide-y text-sm">
        <RatingPart
          label={t('leaderboard.part.fiveS')}
          value={part('fiveS')}
          basis={
            mine.fiveS.walks === 0
              ? t('home.rating.noWalks')
              : `${tp('analytics.shifts.walkCount', mine.fiveS.walks)} · ${tp('analytics.shifts.centreCount', mine.fiveS.centres)}`
          }
          aside={
            mine.fiveS.plantPercentage !== null ? (
              <span className="inline-flex items-center gap-1">
                {plantLabel} {formatPercent(mine.fiveS.plantPercentage)}
                <Hint side="left">{t('home.fiveS.explain')}</Hint>
              </span>
            ) : undefined
          }
        />
        <RatingPart
          label={t('leaderboard.part.followThrough')}
          value={part('followThrough')}
          basis={
            mine.tasks.assigned === 0
              ? t('home.rating.noTasks')
              : t('home.rating.tasksDone', {
                  done: formatNumber(mine.tasks.done),
                  total: formatNumber(mine.tasks.assigned),
                })
          }
          aside={
            mine.tasks.overdue > 0 ? (
              <span className="text-rose-600 dark:text-rose-400">
                {tp('home.tasks.lateCount', mine.tasks.overdue)}
              </span>
            ) : undefined
          }
        />
        <div className="flex items-center justify-between gap-3 py-2">
          <dt className="min-w-0">
            <span className="font-medium">{t('home.rating.proposals')}</span>
            <span className="muted block text-xs">
              {tp('home.rating.accepted', mine.reports.accepted)}
              {' · '}
              {t('home.rating.points', { points: formatNumber(points) })}
            </span>
          </dt>
          <dd className="font-semibold tabular-nums">+{formatNumber(rating.bonus)}</dd>
        </div>
      </dl>
    </Card>
  )
}

function RatingPart({
  label,
  value,
  basis,
  aside,
}: {
  label: string
  value: number | null
  basis: string
  aside?: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <dt className="min-w-0">
        <span className="font-medium">{label}</span>
        <span className="muted block text-xs">{basis}</span>
        {aside && <span className="muted block text-xs">{aside}</span>}
      </dt>
      {/* An em dash rather than 0%: "was on no walk" and "kept machines that
          scored nothing" are different facts, and only one of them is bad. */}
      <dd className="font-semibold tabular-nums">{value === null ? '—' : formatPercent(value)}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function MyTasksCard({ tasks, className }: { tasks: Task[]; className?: string }) {
  const t = useT()

  return (
    <Card className={className}>
      <CardTitle action={<CardLink to={TASKS}>{t('home.tasks.all')}</CardLink>}>
        {t('home.tasks.title')}
      </CardTitle>

      {tasks.length === 0 ? (
        <EmptyState title={t('home.tasks.empty')} description={t('home.tasks.emptyText')} />
      ) : (
        <ul className="divide-border -my-1 divide-y">
          {tasks.map((task) => (
            <li key={task.id}>
              <Link
                to={TASKS}
                className="hover:bg-muted focus-visible:ring-ring/60 -mx-2 flex min-h-control items-center justify-between gap-3 rounded-xl px-2 py-2.5 outline-none transition-colors focus-visible:ring-[3px]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{task.title}</p>
                  <p className={cn('text-xs', task.isOverdue ? 'text-rose-600 dark:text-rose-400' : 'muted')}>
                    {task.dueDate
                      ? t('home.tasks.due', { date: formatDate(task.dueDate) })
                      : t('home.tasks.noDue')}
                  </p>
                </div>
                <Badge color={task.isOverdue ? 'rose' : task.statusColor}>
                  {/* `statusLabel` is the server's — see types/api.ts. Only the
                      overdue override is ours. */}
                  {task.isOverdue ? t('home.tasks.overdue') : task.statusLabel}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/** A card's "see all" — a real link, and tall enough to hit on a phone. */
function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="text-brand-600 focus-visible:ring-ring/60 inline-flex min-h-control shrink-0 items-center rounded-lg text-sm font-medium outline-none hover:underline focus-visible:ring-[3px]"
    >
      {children}
    </Link>
  )
}

/** A large, thumb-sized entry point. Used only on the worker dashboard. */
function BigAction({
  to,
  icon,
  title,
  text,
  primary = false,
  className,
}: {
  to: string
  icon: ReactNode
  title: string
  text: string
  primary?: boolean
  className?: string
}) {
  return (
    <Link
      to={to}
      className={cn(
        'focus-visible:ring-ring/60 flex flex-col justify-center gap-1 rounded-2xl p-5 shadow-sm outline-none focus-visible:ring-[3px]',
        primary
          ? 'bg-primary text-primary-foreground min-h-32 transition-transform active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100'
          : 'bg-card hover:bg-muted min-h-24 border transition-colors sm:min-h-32',
        className,
      )}
    >
      <span className={primary ? '' : 'text-brand-600'}>{icon}</span>
      <span className={cn('mt-1 font-semibold', primary && 'text-lg')}>{title}</span>
      <span className={primary ? 'text-sm opacity-90' : 'muted text-sm'}>{text}</span>
    </Link>
  )
}
